import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Tabs, type Tone } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, day, qty as fmtQty } from '../../lib/format.ts';
import { usePlaces } from '../../lib/places.tsx';
import { useSession } from '../../lib/session.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';
import { parseQty, withUnit, type Unit } from './Stock.tsx';

/**
 * The store room (modules/inventory bins.ts, modules/invoicing warehouse.ts): delivery notes
 * picked from bins, packed and shipped; the bins and what each holds; goods waiting to be put away.
 */

type Stage = 'to_pick' | 'to_pack' | 'to_ship';
const STAGES: Stage[] = ['to_pick', 'to_pack', 'to_ship'];
const STAGE_ICON: Record<Stage, IconName> = { to_pick: 'list', to_pack: 'box', to_ship: 'truck' };
const STAGE_TONE: Record<Stage, Tone> = { to_pick: 'info', to_pack: 'warn', to_ship: 'ok' };

interface Note {
  id: string;
  date: string;
  stage: Stage;
  packages: number | null;
  weight: number | null;
  customer_name: string;
  order_number: number;
  order_year: number;
  reference: string | null;
  town: string | null;
  carrier: string | null;
  vehicle: string | null;
  lines: number;
  units: number;
}
interface Board {
  notes: Note[];
  counts: Record<Stage, number>;
}
interface BinItem {
  item: string;
  name: string;
  unit: Unit;
  qty: number;
}
interface BinRow {
  id: string;
  code: string;
  name: string | null;
  zone: string | null;
  active: boolean;
  items: BinItem[];
  rules: number;
}
interface Waiting {
  item: string;
  name: string;
  unit: Unit;
  qty: number;
  bin: string | null;
  bin_code: string | null;
}
interface Rule {
  id: string;
  item: string;
  name: string;
  unit: Unit;
  bin: string;
  code: string;
}
interface Bins {
  location: string;
  bins: BinRow[];
  to_put_away: Waiting[];
  rules: Rule[];
}
interface PickFrom {
  bin: string | null;
  code: string | null;
  zone: string | null;
  qty: number;
}
interface PickList {
  delivery: {
    id: string;
    stage: Stage;
    date: string;
    customer_name: string;
    packages: number | null;
    weight: number | null;
    picked_at: string | null;
    picked_by_name: string | null;
    packed_at: string | null;
    packed_by_name: string | null;
    picks: { item: string; bin: string | null; qty: number }[] | null;
  };
  lines: { id: string; name: string; unit: Unit; qty: number }[];
  picks: { item: string; name: string; unit: Unit; qty: number; from: PickFrom[]; short: number }[];
}

type Tab = 'pick' | 'bins' | 'away';

export function Warehouse() {
  const { can } = useSession();
  const places = usePlaces();
  const [params, setParams] = useSearchParams();
  const tab = (['pick', 'bins', 'away'].includes(params.get('tab') ?? '') ? params.get('tab') : 'pick') as Tab;
  const [place, setPlace] = useState('');
  const at = places.multi ? place || places.here?.id || '' : '';
  const where = at ? { location: at } : {};
  const ships = can('invoicing.warehouse');
  const { data: board, reload: reloadBoard } = useAction<Board>(ships ? 'invoicing.warehouse' : 'inventory.bins', where);
  const { data: bins, reload: reloadBins } = useAction<Bins>('inventory.bins', where);
  const refresh = async () => {
    await Promise.all([reloadBoard(), reloadBins()]);
  };
  const tabs = [
    ...(ships
      ? [{ key: 'pick' as const, label: t('wh.tab_pick'), icon: 'truck' as const, count: board?.counts ? board.counts.to_pick + board.counts.to_pack + board.counts.to_ship : undefined }]
      : []),
    { key: 'bins' as const, label: t('wh.tab_bins'), icon: 'layers' as const },
    { key: 'away' as const, label: t('wh.tab_away'), icon: 'inbox' as const, count: bins?.to_put_away.length },
  ];
  const shown: Tab = tab === 'pick' && !ships ? 'bins' : tab;

  return (
    <ManageLayout title={t('wh.title')} subtitle={t('wh.subtitle')}>
      <div className="stock">
        <div className="toolbar">
          {places.multi ? (
            <select className="input place-select" value={at} onChange={(e) => setPlace(e.target.value)} aria-label={t('places.location')}>
              {places.places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : null}
          <Tabs tabs={tabs} value={shown} onChange={(k) => setParams(k === 'pick' ? {} : { tab: k }, { replace: true })} label={t('wh.title')} />
        </div>
        {shown === 'pick' ? board && 'notes' in board ? <PickBoard board={board} onChange={refresh} /> : <Skeleton h={320} /> : null}
        {shown === 'bins' ? bins ? <BinsTab data={bins} manage={can('inventory.manage')} moves={can('inventory.count')} onChange={refresh} /> : <Skeleton h={320} /> : null}
        {shown === 'away' ? bins ? <PutAwayTab data={bins} can={can('inventory.count')} onChange={refresh} /> : <Skeleton h={320} /> : null}
      </div>
    </ManageLayout>
  );
}

/* ---------------------------------------------------------------- Pick, pack, ship */

function PickBoard({ board, onChange }: { board: Board; onChange: () => Promise<void> }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!board.notes.length) return <EmptyState icon="truck" title={t('wh.none')} body={t('wh.none_body')} />;
  return (
    <>
      <div className="wh-board">
        {STAGES.map((s) => {
          const notes = board.notes.filter((n) => n.stage === s);
          return (
            <section key={s} className="wh-col" aria-labelledby={`wh-${s}`}>
              <header className="wh-col-head">
                <Icon name={STAGE_ICON[s]} size={16} />
                <h2 id={`wh-${s}`}>{t(`wh.stage_${s}` as MessageKey)}</h2>
                <span className="wh-count">{notes.length}</span>
              </header>
              {notes.length ? (
                notes.map((n) => (
                  <button key={n.id} className="wh-card" onClick={() => setOpen(n.id)} aria-label={`${n.customer_name}, SO ${n.order_number}/${n.order_year}`}>
                    <span className="wh-card-top">
                      <strong>{n.customer_name}</strong>
                      <span className="muted">{day(n.date)}</span>
                    </span>
                    <span className="wh-card-sub">
                      SO {n.order_number}/{n.order_year}
                      {n.reference ? ` · ${n.reference}` : ''}
                      {n.town ? ` · ${n.town}` : ''}
                    </span>
                    <span className="wh-card-foot">
                      <span>{tn('wh.lines', n.lines)}</span>
                      {n.packages ? <span>{tn('wh.packages', n.packages)}</span> : null}
                      {n.weight ? <span>{fmtQty(n.weight)} kg</span> : null}
                    </span>
                  </button>
                ))
              ) : (
                <p className="wh-empty muted">{t(`wh.empty_${s}` as MessageKey)}</p>
              )}
            </section>
          );
        })}
      </div>
      {open ? <PickDialog id={open} onClose={() => setOpen(null)} onChange={onChange} /> : null}
    </>
  );
}

const placeText = (f: PickFrom) => (f.code ? `${f.code}${f.zone ? ` · ${f.zone}` : ''}` : t('wh.no_bin'));

function PickDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<PickList>('invoicing.pick_list', { delivery: id });
  const { busy, run } = useSave();
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [packages, setPackages] = useState('1');
  const [weight, setWeight] = useState('');
  const rows = useMemo(() => (data?.picks ?? []).flatMap((p) => p.from.map((f, i) => ({ key: `${p.item}:${f.bin ?? 'none'}:${i}`, p, f }))), [data]);
  const short = (data?.picks ?? []).filter((p) => p.short > 0);
  const d = data?.delivery;
  const stage = d?.stage ?? 'to_pick';
  const after = async () => {
    await Promise.all([reload(), onChange()]);
  };
  const confirmPick = () =>
    void run(async () => {
      await action('invoicing.confirm_pick', { delivery: id, picks: rows.map((r) => ({ item: r.p.item, bin: r.f.bin, qty: r.f.qty })) });
      await after();
    }, t('wh.picked_ok'));
  const nPackages = Number(packages);
  const nWeight = weight.trim() ? parseQty(weight) : null;
  const packOk = Number.isInteger(nPackages) && nPackages >= 1 && (weight.trim() === '' || (nWeight ?? 0) > 0);
  const confirmPack = () =>
    void run(async () => {
      await action('invoicing.confirm_pack', { delivery: id, packages: nPackages, weight: nWeight });
      await after();
    }, t('wh.packed_ok'));
  const ship = () =>
    void run(async () => {
      await action('invoicing.ship', { delivery: id });
      await onChange();
      onClose();
    }, t('wh.shipped_ok'));
  const unpick = () =>
    void run(async () => {
      await action('invoicing.unpick', { delivery: id });
      setTicked(new Set());
      await after();
    }, t('wh.unpicked_ok'));

  return (
    <Dialog
      title={d ? t('wh.dialog_title', { customer: d.customer_name }) : t('wh.title')}
      onClose={onClose}
      size="lg"
      footer={
        d ? (
          <span className="foot-actions">
            {stage !== 'to_pick' ? (
              <Button variant="ghost" icon="undo" onClick={unpick} disabled={busy}>
                {t('wh.unpick')}
              </Button>
            ) : null}
            {stage === 'to_pick' ? (
              <Button variant="primary" icon="check" onClick={confirmPick} busy={busy} disabled={short.length > 0 || (rows.length > 0 && ticked.size < rows.length)}>
                {t('wh.confirm_pick')}
              </Button>
            ) : stage === 'to_pack' ? (
              <Button variant="primary" icon="box" onClick={confirmPack} busy={busy} disabled={!packOk}>
                {t('wh.confirm_pack')}
              </Button>
            ) : (
              <Button variant="primary" icon="truck" onClick={ship} busy={busy}>
                {t('wh.ship')}
              </Button>
            )}
          </span>
        ) : null
      }
    >
      {!data || !d ? (
        <Skeleton h={200} />
      ) : (
        <div className="wh-pick">
          <ol className="wh-steps" aria-label={t('wh.steps')}>
            {STAGES.map((s, i) => (
              <li key={s} className={s === stage ? 'current' : STAGES.indexOf(stage) > i ? 'done' : ''} aria-current={s === stage ? 'step' : undefined}>
                <Icon name={STAGES.indexOf(stage) > i ? 'check' : STAGE_ICON[s]} size={14} />
                {t(`wh.step_${s}` as MessageKey)}
              </li>
            ))}
          </ol>
          {stage === 'to_pick' ? (
            <>
              <p className="muted">{t('wh.pick_hint')}</p>
              {short.length ? (
                <p className="callout callout-danger" role="alert">
                  <Icon name="alert" size={16} /> {t('wh.short', { list: short.map((p) => `${p.name} (${withUnit(p.short, p.unit)})`).join(', ') })}
                </p>
              ) : null}
              {rows.length ? (
                <div className="table-wrap">
                  <table className="data-table wh-pick-table" aria-label={t('wh.pick_list')}>
                    <thead>
                      <tr>
                        <th scope="col">{t('wh.picked')}</th>
                        <th scope="col">{t('wh.bin')}</th>
                        <th scope="col">{t('wh.item')}</th>
                        <th scope="col" className="num">
                          {t('wh.take')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const on = ticked.has(r.key);
                        return (
                          <tr key={r.key} className={on ? 'wh-done' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={on}
                                aria-label={t('wh.tick', { item: r.p.name, bin: placeText(r.f) })}
                                onChange={() => {
                                  const next = new Set(ticked);
                                  if (on) next.delete(r.key);
                                  else next.add(r.key);
                                  setTicked(next);
                                }}
                              />
                            </td>
                            <td>{r.f.code ? <span className="wh-bin">{r.f.code}</span> : <span className="muted">{t('wh.no_bin')}</span>}</td>
                            <td>
                              {r.p.name}
                              {r.f.zone ? <span className="muted"> · {r.f.zone}</span> : null}
                            </td>
                            <td className="num">
                              <strong>{withUnit(r.f.qty, r.p.unit)}</strong>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="callout callout-info">{t('wh.nothing_to_pick')}</p>
              )}
            </>
          ) : (
            <>
              <p className="callout callout-ok" role="status">
                <Icon name="checkCircle" size={16} /> {t('wh.picked_by', { name: d.picked_by_name ?? '—', at: d.picked_at ? dateTime(d.picked_at) : '' })}
                {d.packed_at ? ` ${t('wh.packed_by', { name: d.packed_by_name ?? '—', n: d.packages ?? 0 })}` : ''}
              </p>
              <ul className="wh-lines">
                {data.lines.map((l) => (
                  <li key={l.id}>
                    <span>{l.name}</span>
                    <strong>{withUnit(l.qty, l.unit)}</strong>
                  </li>
                ))}
              </ul>
              {stage === 'to_pack' ? (
                <div className="form-grid">
                  <Field label={t('wh.packages_label')} required>
                    <input className="input" inputMode="numeric" value={packages} onChange={(e) => setPackages(e.target.value)} />
                  </Field>
                  <Field label={t('wh.weight')} hint={t('wh.weight_hint')}>
                    <input className="input" inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} />
                  </Field>
                </div>
              ) : (
                <p className="muted">{t('wh.ship_hint', { n: d.packages ?? 0, weight: d.weight ? `${fmtQty(d.weight)} kg` : '—' })}</p>
              )}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Bins */

function BinsTab({ data, manage, moves, onChange }: { data: Bins; manage: boolean; moves: boolean; onChange: () => Promise<void> }) {
  const [editing, setEditing] = useState<BinRow | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const shown = data.bins.filter((b) => !q || b.code.toLowerCase().includes(q) || (b.zone ?? '').toLowerCase().includes(q) || b.items.some((i) => i.name.toLowerCase().includes(q)));
  const zones = [...new Set(shown.map((b) => b.zone ?? ''))].sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
  return (
    <section className="list-card" aria-labelledby="wh-bins-title">
      <div className="list-card-head">
        <div>
          <h2 id="wh-bins-title">{t('wh.bins_title')}</h2>
          <p className="muted">{t('wh.bins_hint')}</p>
        </div>
        <span className="foot-actions">
          {data.bins.length > 6 ? (
            <div className="search">
              <Icon name="search" size={18} />
              <input className="input" type="search" placeholder={t('wh.search')} aria-label={t('wh.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          ) : null}
          {manage ? (
            <Button icon="plus" onClick={() => setEditing('new')}>
              {t('wh.new_bin')}
            </Button>
          ) : null}
        </span>
      </div>
      {!data.bins.length ? (
        <EmptyState icon="layers" title={t('wh.no_bins')} body={manage ? t('wh.no_bins_body') : undefined} />
      ) : (
        zones.map((z) => (
          <div key={z || '-'} className="wh-zone">
            <h3 className="wh-zone-title">{z || t('wh.no_zone')}</h3>
            {shown
              .filter((b) => (b.zone ?? '') === z)
              .map((b) => (
                <button key={b.id} className={`list-row stock-row${b.active ? '' : ' archived'}`} onClick={() => setEditing(b)}>
                  <span className="wh-bin wh-bin-lg">{b.code}</span>
                  <span className="list-main">
                    <span className="list-title">{b.name ?? (b.items.length ? tn('wh.items_n', b.items.length) : t('wh.empty_bin'))}</span>
                    <span className="list-sub">
                      {[b.items.length ? b.items.map((i) => `${i.name} ${withUnit(i.qty, i.unit)}`).join(' · ') : null, b.rules ? tn('wh.rules_n', b.rules) : null].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {b.active ? null : <StatusBadge tone="neutral" icon="archive" label={t('wh.closed')} />}
                </button>
              ))}
          </div>
        ))
      )}
      {editing ? <BinDialog bin={editing === 'new' ? null : editing} data={data} manage={manage} moves={moves} onClose={() => setEditing(null)} onChange={onChange} /> : null}
    </section>
  );
}

function BinDialog({ bin, data, manage, moves, onClose, onChange }: { bin: BinRow | null; data: Bins; manage: boolean; moves: boolean; onClose: () => void; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [code, setCode] = useState(bin?.code ?? '');
  const [name, setName] = useState(bin?.name ?? '');
  const [zone, setZone] = useState(bin?.zone ?? '');
  const [moving, setMoving] = useState<BinItem | null>(null);
  const live = bin ? (data.bins.find((b) => b.id === bin.id) ?? bin) : null;
  const rules = data.rules.filter((r) => r.bin === bin?.id);
  const zones = [...new Set(data.bins.map((b) => b.zone).filter((z): z is string => !!z))];
  const save = (active = live?.active ?? true) =>
    void run(
      async () => {
        await action('inventory.save_bin', { bin: bin?.id, location: data.location, code, name: name.trim() || null, zone: zone.trim() || null, active });
        await onChange();
        onClose();
      },
      bin ? t('wh.bin_saved') : t('wh.bin_added'),
    );
  const drop = (rule: Rule) =>
    void run(async () => {
      await action('inventory.delete_putaway_rule', { rule: rule.id });
      await onChange();
    }, t('wh.rule_removed'));
  return (
    <Dialog
      title={bin ? t('wh.bin_title', { code: bin.code }) : t('wh.new_bin')}
      onClose={onClose}
      size="md"
      footer={
        manage ? (
          <span className="foot-actions">
            {live ? (
              <Button variant="ghost" icon={live.active ? 'archive' : 'undo'} onClick={() => save(!live.active)} disabled={busy}>
                {live.active ? t('wh.close_bin') : t('wh.reopen_bin')}
              </Button>
            ) : null}
            <Button variant="primary" icon="check" onClick={() => save()} busy={busy} disabled={!code.trim()}>
              {t('wh.save_bin')}
            </Button>
          </span>
        ) : null
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('wh.code')} hint={t('wh.code_hint')} required>
            <input className="input wh-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} disabled={!manage} maxLength={30} />
          </Field>
          <Field label={t('wh.zone')} hint={t('wh.zone_hint')}>
            <input className="input" value={zone} onChange={(e) => setZone(e.target.value)} disabled={!manage} list="wh-zones" maxLength={40} />
          </Field>
        </div>
        <datalist id="wh-zones">
          {zones.map((z) => (
            <option key={z} value={z} />
          ))}
        </datalist>
        <Field label={t('wh.description')}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} disabled={!manage} maxLength={80} />
        </Field>
        {live ? (
          <>
            <h3 className="wh-sub">{t('wh.holds')}</h3>
            {live.items.length ? (
              <ul className="wh-lines">
                {live.items.map((i) => (
                  <li key={i.item}>
                    <span>{i.name}</span>
                    <strong>{withUnit(i.qty, i.unit)}</strong>
                    {moves ? (
                      <Button size="sm" variant="ghost" icon="move" onClick={() => setMoving(i)} aria-label={t('wh.move_item', { item: i.name })}>
                        {t('wh.move')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t('wh.empty_bin')}</p>
            )}
            {rules.length ? (
              <>
                <h3 className="wh-sub">{t('wh.rules_here')}</h3>
                <ul className="wh-lines">
                  {rules.map((r) => (
                    <li key={r.id}>
                      <span>
                        <Icon name="bookmark" size={14} /> {r.name}
                      </span>
                      {manage ? (
                        <Button size="sm" variant="ghost" icon="x" onClick={() => drop(r)} disabled={busy} aria-label={t('wh.remove_rule', { item: r.name })}>
                          {t('wh.remove')}
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : null}
      </div>
      {moving && live ? <MoveDialog item={moving} from={live} data={data} onClose={() => setMoving(null)} onChange={onChange} /> : null}
    </Dialog>
  );
}

function MoveDialog({ item, from, data, onClose, onChange }: { item: BinItem; from: BinRow; data: Bins; onClose: () => void; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const others = data.bins.filter((b) => b.active && b.id !== from.id);
  const [to, setTo] = useState(others[0]?.id ?? '');
  const [amount, setAmount] = useState(String(item.qty));
  const n = parseQty(amount);
  const ok = !!to && n !== null && n > 0 && n <= item.qty;
  return (
    <Dialog
      title={t('wh.move_title', { item: item.name, code: from.code })}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon="move"
          busy={busy}
          disabled={!ok}
          onClick={() =>
            void run(async () => {
              await action('inventory.move_bin', { item: item.item, from: from.id, to, qty: n });
              await onChange();
              onClose();
            }, t('wh.moved_ok'))
          }
        >
          {t('wh.move')}
        </Button>
      }
    >
      <div className="form-stack">
        <Field label={t('wh.to_bin')}>
          <select className="input" value={to} onChange={(e) => setTo(e.target.value)}>
            {others.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code}
                {b.zone ? ` · ${b.zone}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('wh.qty')} hint={t('wh.holds_n', { qty: withUnit(item.qty, item.unit) })} error={n !== null && n > item.qty ? t('wh.too_much') : null}>
          <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Put away */

function PutAwayTab({ data, can, onChange }: { data: Bins; can: boolean; onChange: () => Promise<void> }) {
  const active = data.bins.filter((b) => b.active);
  if (!data.to_put_away.length) return <EmptyState icon="checkCircle" title={t('wh.all_away')} body={t('wh.all_away_body')} />;
  return (
    <section className="list-card" aria-labelledby="wh-away-title">
      <div className="list-card-head">
        <div>
          <h2 id="wh-away-title">{t('wh.away_title')}</h2>
          <p className="muted">{t('wh.away_hint')}</p>
        </div>
      </div>
      {!active.length ? (
        <p className="callout callout-info">{t('wh.away_no_bins')}</p>
      ) : (
        data.to_put_away.map((w) => <PutAwayRow key={w.item} w={w} bins={active} can={can} location={data.location} onChange={onChange} />)
      )}
    </section>
  );
}

function PutAwayRow({ w, bins, can, location, onChange }: { w: Waiting; bins: BinRow[]; can: boolean; location: string; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [bin, setBin] = useState(w.bin ?? bins.find((b) => b.items.some((i) => i.item === w.item))?.id ?? bins[0]?.id ?? '');
  const [amount, setAmount] = useState(fmtQty(w.qty));
  const [remember, setRemember] = useState(!w.bin);
  const n = parseQty(amount);
  const ok = !!bin && n !== null && n > 0 && n <= w.qty;
  return (
    <div className="wh-away-row">
      <span className="list-main">
        <span className="list-title">{w.name}</span>
        <span className="list-sub">
          {t('wh.waiting', { qty: withUnit(w.qty, w.unit) })}
          {w.bin_code ? ` · ${t('wh.usual_bin', { code: w.bin_code })}` : ''}
        </span>
      </span>
      {can ? (
        <>
          <select className="input" value={bin} onChange={(e) => setBin(e.target.value)} aria-label={t('wh.bin_for', { item: w.name })}>
            {bins.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code}
                {b.zone ? ` · ${b.zone}` : ''}
              </option>
            ))}
          </select>
          <input className="input wh-qty" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label={t('wh.qty_for', { item: w.name })} />
          <label className="check-row wh-remember">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>{t('wh.remember')}</span>
          </label>
          <Button
            icon="check"
            busy={busy}
            disabled={!ok}
            onClick={() =>
              void run(async () => {
                await action('inventory.put_away', { location, item: w.item, bin, qty: n, remember });
                await onChange();
              }, t('wh.away_ok'))
            }
          >
            {t('wh.put_away')}
          </Button>
        </>
      ) : null}
    </div>
  );
}
